import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createContainer } from 'unstated-next'

import { EmbroideryTypeEnum } from '../../__generated__/graphql/catalog/graphql'
import { useLocalization } from '../../app/_providers/LocalizationProvider.client'
import { extractNumericShopifyId } from '../../lib/shopify'
import {
  Embroidery,
  EmbroideryPlacementDetails,
  EmbroideryVariant,
  ProductAsset,
  ProductColor,
  Variant,
} from '../../types/graphql'

import { EmbroideryFormKind, EmbroideryFormOptions, EmbroideryFormValues } from './types'
import {
  EmbroideryPriceRange,
  derivePriceRange,
  generateLineItemProperties,
  getFlatlayImage,
  getFormKind,
  mapEmbroideryOptionToInitialFormValues,
} from './utils'

export interface UseEmbroideryProps {
  /** The possible embroidery options for the current product group. */
  embroideryOptions: ReadonlyArray<Embroidery<EmbroideryVariant | Variant>>
  /** Default embroidery options that should be automatically selected. Used to automatically add a specific embroidery selection to the users product without explicitly opening the embroidery modal. */
  defaultSelectedEmbroideryOptions?: Partial<
    Record<EmbroideryFormKind, EmbroideryFormOptions | null>
  >
  /** Default initial form values that should be used for a given embroidery form. Used for defining a predefined set of field values for a specific embroidery form. */
  defaultInitialFormValues?: {
    [key in keyof EmbroideryFormValues]?: Partial<EmbroideryFormValues[key]>
  }
  /** Individual embroidery fields that should be hidden from the UI. */
  hideFields?: {
    [key in keyof EmbroideryFormValues]?: Array<keyof EmbroideryFormValues[key]>
  }
  /** A list of the product images that are used for the flatlay previews. */
  productImagesWithDefaultFlatlays: ReadonlyArray<ProductAsset>
  /** The currently selected product color. */
  productColor: ProductColor | undefined
  /** The category of the current product group. */
  productCategory: string | undefined
  /** The size of the variant currently selected */
  selectedSize: string | null
  /** Optionally define dropdown options for the text embroidery form. Will replace the free-form text input with a dropdown if defined. */
  textEmbroideryOptions?: {
    secondLine?: string[]
  }
}

// Embroidery 1.5
function useEmbroidery(props: UseEmbroideryProps | undefined) {
  const {
    embroideryOptions = [],
    hideFields,
    defaultSelectedEmbroideryOptions,
    defaultInitialFormValues,
    productImagesWithDefaultFlatlays,
    productColor,
    productCategory,
    selectedSize,
    textEmbroideryOptions,
  } = props ?? {}

  const { locale, region } = useLocalization()
  const editEmbroideryRef = useRef<HTMLButtonElement>(null)
  const productColorHex: string | undefined = productColor?.hexCodes[0]

  // A map of embroidery option kinds => embroidery variant objects
  const embroideryVariantsByKind = useMemo(() => {
    const embroideryVariants: Partial<Record<EmbroideryTypeEnum, EmbroideryVariant | Variant>> = {}
    if (!embroideryOptions) return embroideryVariants

    embroideryOptions.forEach(emb => {
      embroideryVariants[emb.kind] = emb.variants[0]
    })

    return embroideryVariants
  }, [embroideryOptions])

  // Whether at least one embroidery option has been explicitly confirmed by the user
  const [confirmAddEmbroidery, setConfirmAddEmbroidery] = useState<boolean>(false)

  // Derive the initial form values that should be used for each embroidery form.
  const initialFormValuesByKind = useMemo<EmbroideryFormValues>(() => {
    return embroideryOptions.reduce((acc, curr) => {
      const formKind = getFormKind(curr.kind)
      if (!formKind || acc[formKind]) return acc

      const mapped = mapEmbroideryOptionToInitialFormValues(formKind, curr, productColorHex)

      if (defaultInitialFormValues && curr.kind in defaultInitialFormValues) {
        return {
          ...acc,
          [formKind]: {
            ...mapped,
            ...defaultInitialFormValues[formKind],
          },
        }
      }

      return {
        ...acc,
        [formKind]: mapped,
      }
    }, {} as EmbroideryFormValues)
  }, [defaultInitialFormValues, embroideryOptions, productColorHex])

  /* The form values for each embroidery form that has been submitted */
  const [stickyFormValuesByKind, setStickyFormValuesByKind] = useState<
    Partial<EmbroideryFormValues>
  >({})

  /* The values that are stored when a user confirms a specific embroidery selection */
  const [selectedEmbroideryOptions, setSelectedEmbroideryOptions] = useState<
    Partial<Record<EmbroideryFormKind, EmbroideryFormOptions | null>>
  >({})

  /* Normalize the values in `selectedEmbroideryOptions` to a single composite object that is used to generate the embroidery line items */
  const selectedEmbroideryFormOptions = useMemo<EmbroideryFormOptions>(() => {
    let options: EmbroideryFormOptions = {}

    for (const [_key, value] of Object.entries({
      ...defaultSelectedEmbroideryOptions,
      ...selectedEmbroideryOptions,
    })) {
      options = { ...options, ...value }
    }

    // Certain combinations of embroidery kinds require a different SKU that is used downstream
    if (selectedEmbroideryOptions?.TEXT_ONLY) {
      if (selectedEmbroideryOptions.ICON_ONLY) {
        options.embroideryItemVariant = embroideryVariantsByKind?.ICON_TEXT
      }
      if (selectedEmbroideryOptions?.ADD_LOGO) {
        if (
          selectedEmbroideryOptions?.ADD_LOGO.embroideryItemVariant ===
          embroideryVariantsByKind?.LOGO_COLOR
        ) {
          options.embroideryItemVariant = embroideryVariantsByKind?.LOGO_COLOR_TEXT
        }
        if (
          selectedEmbroideryOptions?.ADD_LOGO.embroideryItemVariant ===
          embroideryVariantsByKind?.LOGO_BLACK_WHITE
        ) {
          options.embroideryItemVariant = embroideryVariantsByKind?.LOGO_BLACK_WHITE_TEXT
        }
      }
      if (selectedEmbroideryOptions.LOGO_ONLY) {
        options.embroideryItemVariant = embroideryVariantsByKind?.LOGO_TEXT
      }
    }

    return options
  }, [selectedEmbroideryOptions, defaultSelectedEmbroideryOptions, embroideryVariantsByKind])

  /* Generate the necessary line items for the selected embroidery options */
  const embroideryLineItemProperties = useMemo(() => {
    if (Object.keys(selectedEmbroideryFormOptions).length === 0) return undefined

    const variantId: number | undefined =
      (selectedEmbroideryFormOptions?.embroideryItemVariant?.shopifyId &&
        extractNumericShopifyId(selectedEmbroideryFormOptions?.embroideryItemVariant?.shopifyId)) ||
      undefined
    const logoSetupId: number | undefined =
      (selectedEmbroideryFormOptions?.embroiderySetupVariant?.shopifyId &&
        extractNumericShopifyId(
          selectedEmbroideryFormOptions?.embroiderySetupVariant?.shopifyId
        )) ||
      undefined
    return generateLineItemProperties(
      selectedEmbroideryFormOptions,
      productColorHex,
      variantId,
      logoSetupId
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmAddEmbroidery, selectedEmbroideryFormOptions, productColorHex, selectedSize])

  const priceRange: EmbroideryPriceRange = useMemo((): EmbroideryPriceRange => {
    return derivePriceRange(embroideryOptions, region, locale, 'cents')
  }, [embroideryOptions, locale, region])

  const toggleConfirmAddEmbroidery = useCallback(() => {
    setConfirmAddEmbroidery(current => !current)
  }, [])

  const [previewItemsByKind, setPreviewItemsByKind] = useState<
    Partial<Record<EmbroideryFormKind, JSX.Element[]>>
  >({})

  const clearSelectedEmbroideryFormOptions = useCallback(() => {
    setConfirmAddEmbroidery(false)
    setPreviewItemsByKind({})
    setSelectedEmbroideryOptions(defaultSelectedEmbroideryOptions ?? {})
    setStickyFormValuesByKind({})
  }, [defaultSelectedEmbroideryOptions])

  const [selectedFormKind, setSelectedFormKind] = useState<EmbroideryFormKind | null>(null)

  const [selectedPlacements, setSelectedPlacements] = useState<
    Partial<Record<EmbroideryFormKind, EmbroideryPlacementDetails>>
  >({})

  const choosePlacement = useCallback(
    (embroideryKind: EmbroideryTypeEnum | undefined, placement: EmbroideryPlacementDetails) => {
      if (!embroideryKind) return
      setSelectedPlacements(prev => ({
        ...prev,
        [embroideryKind]: placement,
      }))
    },
    []
  )

  const currentPlacement = useMemo(() => {
    if (selectedFormKind === null) return null

    // If there is an explicitly selected placement set for the user
    const selectedPlacement = selectedPlacements[selectedFormKind]
    if (selectedPlacement) return selectedPlacement ?? null

    // Fallback to use the default placements defined by the form state
    const formValues = initialFormValuesByKind[selectedFormKind]
    if (formValues && 'logoPlacement' in formValues) {
      return formValues.logoPlacement ?? null
    }
    if (formValues && 'iconPlacement' in formValues) {
      return formValues.iconPlacement ?? null
    }
    if (formValues && 'textPlacement' in formValues) {
      return formValues.textPlacement ?? null
    }

    return selectedPlacements[selectedFormKind] ?? null
  }, [selectedFormKind, initialFormValuesByKind, selectedPlacements])

  const backgroundImage = getFlatlayImage({
    images: productImagesWithDefaultFlatlays,
    placement: currentPlacement,
    productColor: productColor,
  })

  const currentlySelectedFormOption = useMemo(() => {
    return selectedFormKind !== null
      ? embroideryOptions.find(({ kind }) => kind === selectedFormKind)
      : undefined
  }, [embroideryOptions, selectedFormKind])

  // check if the selected options are valid for the currently selected product color
  const currentSelectedOptionsValid = useMemo(() => {
    let isValid = true
    embroideryOptions.forEach(option => {
      const formKind = getFormKind(option.kind)
      if (!formKind) return

      const selectedOption = selectedEmbroideryOptions[formKind]
      if (selectedOption) {
        switch (option.kind) {
          case 'TEXT_ONLY': {
            if (
              selectedOption['textColor'] &&
              !option['textColors']?.includes(selectedOption['textColor'])
            ) {
              isValid = false
            }
            if (!option['textFonts']?.find(opt => opt.name === selectedOption['textFont'])) {
              isValid = false
            }
            if (
              !option['textPlacements']?.find(opt => opt.name === selectedOption['textPlacement'])
            ) {
              isValid = false
            }
            break
          }
          case 'ICON_ONLY': {
            if (
              selectedOption['iconInitialsColor'] &&
              !option['initialsColors']?.includes(selectedOption['iconInitialsColor'])
            ) {
              isValid = false
            }
            if (
              !option['iconPlacements']?.find(opt => opt.name === selectedOption['iconPlacement'])
            ) {
              isValid = false
            }
            break
          }
        }
      }
    })
    return isValid
  }, [embroideryOptions, selectedEmbroideryOptions])

  /* Reset form values if the current form options are not valid for the given product */
  useEffect(() => {
    if (!currentSelectedOptionsValid) {
      clearSelectedEmbroideryFormOptions()
    }
  }, [currentSelectedOptionsValid, clearSelectedEmbroideryFormOptions])

  /* Reset form values if the available embroidery options have changed */
  const embroideryOptionsKey = embroideryOptions.map(({ kind }) => kind).join()
  useEffect(() => {
    clearSelectedEmbroideryFormOptions()
  }, [clearSelectedEmbroideryFormOptions, embroideryOptionsKey])

  return {
    backgroundImage,
    choosePlacement,
    clearSelectedEmbroideryFormOptions,
    confirmAddEmbroidery,
    currentPlacement,
    currentlySelectedFormOption,
    editEmbroideryRef,
    defaultSelectedEmbroideryOptions,
    embroideryLineItemProperties,
    embroideryOptions,
    hideFields,
    previewItemsByKind,
    priceRange,
    productCategory,
    productColorHex,
    selectedEmbroideryFormOptions,
    selectedEmbroideryOptions,
    setSelectedEmbroideryOptions,
    selectedFormKind,
    setConfirmAddEmbroidery,
    setPreviewItemsByKind,
    setSelectedFormKind,
    setStickyFormValuesByKind,
    stickyFormValuesByKind,
    initialFormValuesByKind,
    toggleConfirmAddEmbroidery,
    embroideryVariantsByKind,
    selectedSize,
    textEmbroideryOptions,
  }
}

export const EmbroideryService = createContainer(useEmbroidery)
